import { h, toast } from '../dom';
import { api, putFile, type PrivacySettings, type User } from '../../api/client';
import { avatar, guard, LEVELS, section, sheet, skeleton } from '../components';
import { shell } from '../shell';
import { dropSession, login, logout } from '../../auth/oidc';
import { saveImage } from '../share';
import { navigate } from '../router';
import { avatarJpeg } from '../../media/image';
import { profileHeader } from './profile';
import { pushRow } from '../pushui';

export function renderMe(root: HTMLElement, me: User, onChange: (u: User) => void) {
  const content = shell(root, { title: 'Tôi', tab: '#/me' });

  // ---- avatar upload (center crop 512² → upload ticket → PUT → set)
  const file = h('input', { type: 'file', accept: 'image/*', hidden: true });
  const avBox = h('span.ph-avwrap', {}, avatar(me, 104));
  const avBtn = h('button.ph-avatar.editable', { 'aria-label': 'Đổi ảnh đại diện', title: 'Đổi ảnh đại diện', onclick: () => file.click() },
    avBox, h('span.ph-cam', { 'aria-hidden': 'true' }, '📷'));
  file.onchange = async () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f) return;
    avBtn.classList.add('busy');
    avBtn.disabled = true;
    try {
      const jpeg = await avatarJpeg(f);
      const preview = URL.createObjectURL(jpeg);
      avBox.replaceChildren(h('span.avatar', { style: { width: '104px', height: '104px' } }, h('img', { src: preview, alt: '' })));
      const t = await api.avatarTicket();
      await putFile(t.uploadUrl, jpeg);
      const u = await api.setAvatar(t.objectKey);
      me = u;
      onChange(u);
      toast('Đã đổi ảnh đại diện ♡');
    } catch (e) {
      toast((e as Error).message || 'Không đổi được ảnh', 3500);
      avBox.replaceChildren(avatar(me, 104));
    } finally {
      avBtn.classList.remove('busy');
      avBtn.disabled = false;
    }
  };

  // ---- profile edit
  const name = h('input.field', { value: me.displayName, maxLength: 60, placeholder: 'Tên hiển thị', 'aria-label': 'Tên hiển thị' });
  const username = h('input.field', { value: me.username ?? '', maxLength: 30, placeholder: 'username (a-z, 0-9, . _)', autocapitalize: 'off', 'aria-label': 'Username' });
  const bio = h('textarea.field', { rows: 2, maxLength: 300, placeholder: 'Giới thiệu ngắn', 'aria-label': 'Giới thiệu' }, me.bio ?? '');
  const save = h('button.primary', {}, 'Lưu');
  save.onclick = async () => {
    const u = await guard(api.updateMe({ displayName: name.value.trim(), username: username.value.trim() || undefined, bio: bio.value }), 'Đã lưu');
    if (u) { me = u; onChange(u); }
  };
  const edit = h('details.card.editcard', {},
    h('summary', {}, h('b', {}, '✏️ Sửa hồ sơ'), h('span.chev', { 'aria-hidden': 'true' }, '›')),
    h('label.flabel', {}, 'Tên hiển thị', name),
    h('label.flabel', {}, 'Username', username),
    h('label.flabel', {}, 'Giới thiệu', bio),
    save);

  const closeness = h('input', { type: 'checkbox', checked: true });

  // ---- Settings → Quyền riêng tư: how long friends see my posts
  const expire = h('input', { type: 'checkbox', checked: true, disabled: true }) as HTMLInputElement;
  const autoDel = h('input', { type: 'checkbox', checked: false, disabled: true }) as HTMLInputElement;
  const applyPrivacy = (s: PrivacySettings) => {
    closeness.checked = s.closenessEnabled;
    expire.checked = s.postsExpire;
    autoDel.checked = s.autoDeletePosts;
    expire.disabled = s.autoDeletePosts; // auto-delete implies the 24 h limit
    autoDel.disabled = false;
  };
  void api.mySettings().then(applyPrivacy).catch(() => {});
  const savePrivacy = async (patch: Parameters<typeof api.updateSettings>[0], ok: string, revert: () => void) => {
    expire.disabled = autoDel.disabled = true;
    const s = await guard(api.updateSettings(patch), ok);
    if (s) applyPrivacy(s);
    else { revert(); expire.disabled = autoDel.checked; autoDel.disabled = false; }
  };
  expire.onchange = () => {
    const on = expire.checked;
    void savePrivacy({ postsExpire: on },
      on ? 'Bạn bè chỉ thấy bài đăng trong 24 giờ' : 'Bạn bè thấy bài đăng không giới hạn', () => { expire.checked = !on; });
  };
  autoDel.onchange = () => {
    const on = autoDel.checked;
    if (on && !confirm('Bài đăng cũ hơn 24 giờ, cùng bình luận và cảm xúc, sẽ bị xoá hẳn (kể cả các bài hiện có). Bộ ảnh vẫn còn trong Kỷ niệm. Bật?')) {
      autoDel.checked = false;
      return;
    }
    void savePrivacy({ autoDeletePosts: on },
      on ? 'Đã bật tự xoá bài đăng sau 24 giờ' : 'Đã tắt tự xoá', () => { autoDel.checked = !on; });
  };
  const privacyRow = (input: HTMLInputElement, title: string, sub: string) =>
    h('label.urow.toggle', {}, h('div.umain', {}, h('b', {}, title), h('small', {}, sub)), input);
  closeness.onchange = async () => {
    const on = closeness.checked;
    await guard(api.setClosenessEnabled(on), on ? 'Đã bật độ thân mật' : 'Đã tắt và xoá điểm thân mật');
  };

  // quick stats: friends + best streak
  const statFriends = h('b', {}, '…');
  const statStreak = h('b', {}, '…');
  void api.friends().then((f) => {
    statFriends.textContent = String(f.length);
    const best = Math.max(0, ...f.map((x) => x.streak ?? 0));
    statStreak.textContent = best ? `🔥 ${best}` : '–';
  }).catch(() => { statFriends.textContent = '–'; statStreak.textContent = '–'; });

  const link = (href: string, icon: string, title: string, sub: string) =>
    h('a.urow', { href }, h('span.roomicon', { 'aria-hidden': 'true' }, icon),
      h('div.umain', {}, h('b', {}, title), h('small', {}, sub)), h('span.chev', { 'aria-hidden': 'true' }, '›'));

  // ---- Luật BVDLCN: tải dữ liệu của tôi / xoá tài khoản
  const exportBtn = h('button.urow', {}, h('span.roomicon', { 'aria-hidden': 'true' }, '⬇'),
    h('div.umain', {}, h('b', {}, 'Tải dữ liệu của tôi'), h('small', {}, 'Một file JSON gồm hồ sơ, bạn bè, bài đăng, bình luận')));
  exportBtn.onclick = async () => {
    exportBtn.disabled = true;
    toast('Đang chuẩn bị dữ liệu…');
    try {
      const data = await api.exportData();
      await saveImage(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), 'booth-together-du-lieu.json');
      toast('Đã tải dữ liệu của bạn');
    } catch (e) {
      toast(`Không tải được dữ liệu: ${(e as Error).message}`, 4000);
    } finally {
      exportBtn.disabled = false;
    }
  };
  const deleteBtn = h('button.urow.danger', {}, h('span.roomicon', { 'aria-hidden': 'true' }, '🗑'),
    h('div.umain', {}, h('b', {}, 'Xoá tài khoản'), h('small', {}, 'Xoá vĩnh viễn tài khoản và toàn bộ dữ liệu')));
  deleteBtn.onclick = () => {
    const word = h('input.field', { placeholder: 'Gõ XOA để xác nhận', autocapitalize: 'characters', 'aria-label': 'Gõ XOA để xác nhận' });
    const go = h('button.primary.danger', { disabled: true }, 'Xoá vĩnh viễn');
    word.oninput = () => { go.disabled = word.value.trim() !== 'XOA'; };
    const close = sheet('Xoá tài khoản?', h('div.share', {},
      h('p', {}, 'Ảnh đại diện, bài đăng, bình luận, cảm xúc, bạn bè và ảnh tách nền của bạn sẽ bị xoá vĩnh viễn. Các dải ảnh chụp chung sẽ được tạo lại không có bạn. Không thể hoàn tác.'),
      word, go));
    go.onclick = async () => {
      go.disabled = true;
      go.textContent = 'Đang xoá…';
      try {
        await api.deleteAccount();
      } catch (e) {
        toast(`Chưa xoá được: ${(e as Error).message}`, 4500);
        go.disabled = false;
        go.textContent = 'Xoá vĩnh viễn';
        return;
      }
      close();
      toast('Đã xoá dữ liệu. Xác nhận lần cuối để xoá tài khoản đăng nhập…', 4000);
      dropSession();
      // Keycloak's own confirmation page deletes the login identity, then returns to the app
      await login({ kcAction: 'delete_account', returnTo: '#/login' }).catch(() => navigate('#/login'));
    };
  };

  content.append(
    profileHeader(me, {
      avatarEl: avBtn,
      stats: [
        h('a.stat', { href: '#/friends' }, statFriends, h('small', {}, 'bạn bè')),
        h('div.stat', {}, statStreak, h('small', {}, 'chuỗi chụp chung')),
      ],
    }),
    file,
    h('section.ulist', {},
      link('#/memories', '🗓', 'Kỷ niệm', 'Lịch ảnh và các buổi chụp'),
      link('#/memories?tab=people', '🫶', 'Đã chụp cùng', 'Những người bạn đã chụp booth chung'),
      link('#/library', '🗂', 'Thư viện ảnh', 'Mọi dải ảnh đã chụp'),
      link('#/rooms', '🎞', 'Phòng chụp', 'Tạo phòng, nhập mã, lời mời')),
    edit,
    h('section.card', {}, h('h3', {}, 'Những người thân nhất 💞'),
      h('p.tiny.left', {}, 'Chỉ mình bạn thấy. Tính từ việc chụp chung, mời nhau, bình luận, thả cảm xúc — giảm dần nếu lâu không tương tác.'),
      section(() => api.topFriends(5), (top) => top.length
        ? h('div.ulist.flat', {}, ...top.map((t, i) => h('a.urow', { href: `#/u/${t.user.id}` },
          h('span.rank', {}, `${i + 1}`), avatar(t.user, 40),
          h('div.umain', {}, h('b', {}, t.user.displayName), h('small', {}, `${t.sessionsTogether} lần chụp chung`)),
          h('span.level', {}, LEVELS[t.level].icon, ' ', LEVELS[t.level].name))))
        : h('p.tiny', {}, 'Chụp cùng bạn bè để thấy ai thân với bạn nhất ♡'), () => skeleton('row', 3)),
      h('label.check', {}, closeness, ' Bật tính độ thân mật')),
    h('section.ulist', {},
      pushRow(),
      link('#/settings', '⚙', 'Cài đặt máy chủ', 'Địa chỉ máy chủ Booth')),
    h('h3', {}, 'Quyền riêng tư và dữ liệu'),
    h('section.ulist', {},
      privacyRow(expire, 'Bạn bè chỉ thấy bài đăng trong 24 giờ',
        'Sau đó chỉ bạn và người trong ảnh còn xem được, trong Kỷ niệm và trang cá nhân'),
      privacyRow(autoDel, 'Tự xoá hẳn bài đăng sau 24 giờ',
        'Xoá cả bình luận và cảm xúc. Bộ ảnh chụp vẫn nằm riêng trong Kỷ niệm của bạn')),
    h('section.ulist', {},
      link('#/privacy', '🔒', 'Chính sách quyền riêng tư', 'Dữ liệu nào được thu thập và vì sao'),
      exportBtn,
      deleteBtn),
    h('section.ulist', {},
      h('button.urow.danger', {
        onclick: async () => {
          await logout();
          toast('Đã đăng xuất');
          navigate('#/login');
        },
      }, h('span.roomicon', { 'aria-hidden': 'true' }, '⎋'), h('div.umain', {}, h('b', {}, 'Đăng xuất')))),
    h('p.tiny.center', {}, 'Booth Together · v0.9.7'),
  );
}
